import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { genreColor, genreDonutChart } from "./charts";
import { formatNumber, percent } from "../../../../utils/numbers";
import { useGenreViews } from "./useDashboardData";
import {
  DateRangeFields,
  IncompleteViewsNote,
  PeriodSelect,
  usePeriodFilter,
} from "./PeriodFilter";

const CATEGORIES = [
  { value: "movie", label: "Videos" },
  { value: "podcast", label: "Podcasts" },
  { value: "tvshow", label: "TV shows" },
];

/**
 * Views per genre of a category over a period (`unique_viewers` from the database):
 * one bar per genre with views, and the same genres in a donut.
 */
const TopGenres = () => {
  const { t } = useTranslation();
  const [category, setCategory] = useState("movie");
  const periodFilter = usePeriodFilter();
  const { loading, data } = useGenreViews(category, periodFilter.filter);

  // Genres without views over the period are left out
  const genres = (data?.list || [])
    .filter((genre) => genre.count > 0)
    .map((genre, index) => ({ ...genre, color: genreColor(index) }));
  const max = Math.max(...genres.map((genre) => genre.count), 0);
  const chart = genreDonutChart(
    genres.map((genre) => genre.name),
    genres.map((genre) => genre.count),
    t("views"),
    genres.map((genre) => genre.color)
  );

  return (
    <section className="dash-card">
      <div className="dash-card-header">
        <h2 className="dash-card-title">{t("Most viewed genres")}</h2>
        <div className="dash-controls">
          <div
            className="dash-segmented"
            role="group"
            aria-label={t("Category")}
          >
            {CATEGORIES.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={category === value}
                onClick={() => setCategory(value)}
              >
                {t(label)}
              </button>
            ))}
          </div>
          <PeriodSelect {...periodFilter} label={t("Period of the genres")} />
        </div>
      </div>

      <DateRangeFields {...periodFilter} />

      {loading && !data ? (
        <div className="dash-skeleton" style={{ height: 260 }} />
      ) : genres.length > 0 ? (
        <div className="dash-genres" aria-busy={loading}>
          <div className="dash-genre-list">
            {genres.map((genre) => (
              <div className="dash-genre" key={genre._id}>
                <div className="dash-genre-row">
                  <span className="dash-genre-name">{genre.name}</span>
                  <span className="dash-genre-count">
                    {formatNumber(genre.count)} {t("views")}
                  </span>
                </div>
                <div className="dash-genre-track">
                  <span
                    style={{
                      width: `${percent(genre.count, max)}%`,
                      background: genre.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <Chart
            options={chart.options}
            series={chart.series}
            type="donut"
            height="260"
          />
        </div>
      ) : (
        <p className="dash-empty">
          {data ? t("No views over this period") : t("Not available")}
        </p>
      )}

      <IncompleteViewsNote data={data} />
    </section>
  );
};

export default TopGenres;
